import React, { useLayoutEffect, useRef, useState } from 'react';
import { AvTooltip, filterOperatorOptions, operatorColors } from '@fabric-ui/components';
import { Box } from '@mui/material';
import { cn } from '@utils/cn';

export const FieldFilterBoxSelectExpression = ({ rawExpression }) => {
  const operator = rawExpression.and ? 'and' : rawExpression.or ? 'or' : undefined;

  if (!operator) {
    return null;
  }

  const operands = rawExpression[operator];
  const [visibleOperands, setVisibleOperands] = useState(operands.length);
  const [remainingCount, setRemainingCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const calculateOperandsLength = () => {
      if (containerRef.current && containerRef.current.parentElement) {
        const moreTagWidth = 40;
        const operandWidths = Array.from(containerRef.current?.querySelectorAll('.operand-item')).map(el => el.clientWidth);
        const parentEl = containerRef.current.parentElement;
        const computedStyle = window.getComputedStyle(parentEl);
        const gap = parseFloat(computedStyle.gap) || 0;
        const containerWidth =
          parentEl.clientWidth - (parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight)) - gap;

        if (operandWidths.length > 0) {
          // eslint-disable-next-line fabric-ui/no-let
          let operandsCount = 0;
          // eslint-disable-next-line fabric-ui/no-let
          let totalOperandsWidth = 0;

          // eslint-disable-next-line no-restricted-syntax
          for (const operandWidth of operandWidths) {
            if (containerWidth - moreTagWidth - gap < totalOperandsWidth + operandWidth + gap) {
              break;
            }

            totalOperandsWidth += operandWidth;
            operandsCount++;
          }

          setVisibleOperands(operandsCount);
          setRemainingCount(operands.length - operandsCount);
        }
      }
    };

    calculateOperandsLength();
    const resizeObserver = new ResizeObserver(calculateOperandsLength);

    if (containerRef?.current?.parentElement) {
      resizeObserver.observe(containerRef.current.parentElement);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, operands]);

  const renderOperand =
    (shouldRenderHidden?: boolean) =>
    // eslint-disable-next-line react/no-unstable-nested-components
    ({ value, operator: op }, index) => (
      <Box className={cn('operand-item flex gap-1', !shouldRenderHidden && index >= visibleOperands && 'invisible absolute')} key={value}>
        {index > 0 && (
          <span className="font-semibold" style={{ color: operatorColors[operator!] }}>
            {operator.toUpperCase()}
          </span>
        )}
        <Box className="flex gap-1">
          <Box className="font-semibold">{filterOperatorOptions.string.find(({ value }) => value === op)?.title}</Box>
          {typeof value === 'string' ? <div>&quot;{value}&quot;</div> : null}
        </Box>
      </Box>
    );

  return (
    <Box ref={containerRef} className="flex">
      <Box className={cn('flex gap-1 text-content-base-primary', visibleOperands > 0 && 'mr-2')}>{operands.map(renderOperand())}</Box>
      {remainingCount > 0 && (
        <AvTooltip
          title={
            <Box className="bg-surface-immutable-darkGrey text-content-immutable-white flex flex-wrap gap-1">
              {operands.map(renderOperand(true))}
            </Box>
          }>
          <Box
            key="operands-chip"
            className="rounded-[20px] bg-surface-interactive-secondary-default px-[6px] font-medium text-content-base-secondary">
            +{remainingCount}
          </Box>
        </AvTooltip>
      )}
    </Box>
  );
};
